import React from 'react';
import {Avatar, Button, Space, Tooltip} from 'antd';
import UserRoleSelect from '../../components/pages/TeamPage/tabComponents/UserRoleSelect';
import UserDetails from '../../components/UserDetails';
import {userStatusesConstants, userRolesConstants, userGroupConstants} from '../../constants';
import {objectHelpers} from './object.helpers';
import {textHelpers} from './text.helpers';

const {ACTIVE, DISABLED, EXTERNAL, PENDING} = userStatusesConstants;
const {ADMIN, BOOKKEEPER, MANAGER, SUPPORT, USER} = userRolesConstants;

const gObjProp = objectHelpers.getObjProp;

const userRoles = {
  [ADMIN]: {
    key: 'userRoles.admin',
    color: '#52C41A'
  },
  [MANAGER]: {
    key: 'userRoles.manager',
    color: '#D9D9D9',
  },
  [BOOKKEEPER]: {
    key: 'userRoles.bookkeeper',
    color: '#D9D9D9',
  },
  [USER]: {
    key: 'userRoles.user',
    color: '#D9D9D9'
  },
  [SUPPORT]: {
    key: 'userRoles.support',
    color: '#D9D9D9'
  },
};


/**
 * Collection of helper utilities for the Teams domain.
 */
export const teamsHelpers = {
  /**
   * Returns a UserDetails element for the given employee object.
   * @param {Object|*} employee - The employee object.
   * @returns {React.ReactElement|null} A UserDetails component, or null if employee is not an object.
   * @example
   * const details = teamsHelpers.getEmployeeDetails(employee);
   */
  getEmployeeDetails: (employee) => {
    if (employee && typeof employee === 'object') {
      return <UserDetails user={employee} />;
    } else {
      return null;
    }
  },
  /**
   * Returns an Avatar with a Tooltip showing the user's full name or email.
   * @param {Object|null} user - User object with full_name, email, and optional logo.
   * @param {Object} [avatarProps={}] - Extra props forwarded to the Avatar (e.g. size).
   * @returns {React.ReactElement|string} Tooltip-wrapped Avatar or empty string if no user.
   * @example
   * teamsHelpers.getUserAvatar(user, {size: 24});
   */
  getUserAvatar: (user, avatarProps = {}) => {
    if (!user) return '';
    const title = user.full_name || user.email;
    return (
      <Tooltip title={title}>
        <Avatar src={user.logo || undefined} {...avatarProps}>
          {textHelpers.getInitials(title)}
        </Avatar>
      </Tooltip>
    );
  },
  /**
   * Returns an Avatar with a Tooltip showing the employee's full name or email for the given ID.
   * @param {Object} [params={}] - Parameters object.
   * @param {string} params.value - Employee ID to look up.
   * @param {Array<Object>} [params.employees=[]] - List of employee objects.
   * @param {Object} [params.avatarProps] - Extra props forwarded to the Avatar (e.g. size).
   * @returns {React.ReactElement|string} Tooltip-wrapped Avatar or empty string if not found.
   * @example
   * teamsHelpers.getEmployeeAvatar({ value: 'emp-1', employees, avatarProps: {size: 24} });
   */
  getEmployeeAvatar: ({value, employees = [], avatarProps = {}} = {}) =>
    teamsHelpers.getUserAvatar(employees.find(e => e.employee_id === value), avatarProps),
  /**
   * Returns a translated user group label based on the employee's account status.
   * @param {Object} employee - The employee object.
   * @param {string} employee.account_status - The employee's account status.
   * @param {Function} t - i18next translation function.
   * @returns {string} Translated group label, or empty string if the status is not mapped.
   * @example
   * const label = teamsHelpers.getUserGroupLabel(employee, t);
   */
  getUserGroupLabel: (employee, t) => {
    const userGroup = {
      [userStatusesConstants.ACTIVE]: userGroupConstants.ACTIVE,
      [userStatusesConstants.PENDING]: userGroupConstants.EXTERNAL,
      [userStatusesConstants.EXTERNAL]: userGroupConstants.EXTERNAL,
      [userStatusesConstants.DISABLED]: userGroupConstants.DISABLED,
    }[employee?.account_status];
    return userGroup !== undefined ? t(`main:userGroups.${userGroup}`) : '';
  },
  /**
   * Returns a UserRoleSelect element for the given employee.
   * @param {Object} options
   * @param {Object} options.employee - The employee object with role, email, and team_ids.
   * @param {Function} [options.onChange] - Callback fired when the role selection changes.
   * @returns {React.ReactElement|null} A UserRoleSelect component, or null if employee is not an object.
   * @example
   * const roleSelect = teamsHelpers.getRole({ employee, onChange: handleRoleChange });
   */
  getRole: ({employee, onChange}) => {
    if (employee && typeof employee === 'object') {
      return (
        <UserRoleSelect
          defaultValue={employee.role}
          email={employee.email}
          teamIds={employee.team_ids}
          onClick={(e) => e.stopPropagation()}
          onChange={(value) => onChange?.(value)}
        />
      )
    } else {
      return null;
    }
  },
  /**
   * Returns action button(s) appropriate for the employee's current account status.
   * @param {Object} options
   * @param {Object} options.employee - The employee object with account_status, email, and role.
   * @param {string} options.editorEmail - Email of the user performing the action; self-editing is suppressed.
   * @param {Function} options.t - i18next translation function.
   * @param {Function} options.onClick - Callback fired on button click, receives {e, action, employee}.
   * @param {boolean} options.isEditorSupport - When true, hides destructive actions reserved for non-support editors.
   * @returns {React.ReactElement|null|undefined} A Space element with action buttons, null, or undefined.
   * @example
   * const actions = teamsHelpers.getActions({ employee, editorEmail, t, onClick, isEditorSupport: false });
   */
  getActions: ({employee, editorEmail, t, onClick, isEditorSupport}) => {
    if (employee && typeof employee === 'object') {
      const status = gObjProp(employee, 'account_status');
      const email = gObjProp(employee, 'email');
      const role = gObjProp(employee, 'role');
      const isSupportRole = role === SUPPORT;
      if (email === editorEmail) return ;

      const buttons = {
        [ACTIVE]: !isEditorSupport && (
          <Button
            danger
            onClick={(e) => onClick({e, action: 'disable', employee})}
            type='text'
          >
            {t('main:disable')}
          </Button>
        ),
        [DISABLED]: !isEditorSupport && (
          <Button
            danger
            onClick={(e) => onClick({e, action: 'enable', employee})}
            type='text'
          >
            {t('main:enable')}
          </Button>
        ),
        [EXTERNAL]: (
          <Button
            className='primary'
            onClick={(e) => onClick({e, action: 'invite', employee})}
            type='text'
          >
            {t('main:invite')}
          </Button>
        ),
        [PENDING]: (
          <>
            <Button
              className='primary'
              onClick={(e) => onClick({e, action: 'resend-invite', employee})}
              type='text'
            >
              {t('teams:resendInvite')}
            </Button>
            {!isEditorSupport && (
              <Button
                danger
                onClick={(e) => onClick({e, action: 'delete', employee})}
                type='text'
              >
                {t('main:cancel')}
              </Button>
            )}
          </>
        ),
      };
      // hide actions is editor has support role && edited user has support role as well
      if (isSupportRole && !isEditorSupport && [ACTIVE, DISABLED].includes(status)) return ;
      return buttons.hasOwnProperty(status) ? (
        <Space className='actions'>
          {buttons[status]}
        </Space>
      ) : null;
    } else {
      return null;
    }
  },
  /**
   * Returns the status descriptor object for a given user role key.
   * @param {string} role - A userRolesConstants value.
   * @returns {{key: string, color: string}|undefined} The role descriptor, or undefined if not found.
   * @example
   * const roleInfo = teamsHelpers.getUserRole(userRolesConstants.ADMIN);
   */
  getUserRole: (role) => objectHelpers.getStatus(role, userRoles),
  /**
   * Builds Ant Design Modal.confirm props for a destructive employee action.
   * @param {Object} options
   * @param {Function} options.t - i18next translation function.
   * @param {string} options.action - The action label shown in the modal title.
   * @param {Object} options.employee - The employee being acted upon.
   * @param {string} [options.employee.full_name] - Employee's full name.
   * @param {string} [options.employee.email] - Employee's email (fallback when full_name is absent).
   * @param {...*} rest - Additional props merged into the returned object.
   * @returns {Object} Props object compatible with Ant Design Modal.confirm.
   * @example
   * const modalProps = teamsHelpers.getConfirmModalProps({ t, action: 'disable', employee });
   */
  getConfirmModalProps: ({t, action, employee, ...rest}) => ({
    cancelButtonProps: {size: 'large', className: 'green-btn-outlined'},
    cancelText: t('no'),
    icon: null,
    okButtonProps: {size: 'large', type: 'primary'},
    okText: t('yes'),
    title: t('teams:modal.actionModal.title', {action, name: employee?.full_name || employee?.email}),
    ...rest
  }),
  /**
   * Returns an updated employee object reflecting a new re-invitation attempt.
   * @param {Object} employee - The employee object.
   * @param {string} employee.invitation_attempts - JSON-encoded list of past invitation Unix timestamps.
   * @returns {Object} Updated employee with account_status set to PENDING and new timestamp appended.
   * @example
   * const updated = teamsHelpers.getReInvitedUserData(employee);
   */
  getReInvitedUserData: (employee) => {
    let invitations = employee.invitation_attempts;
    invitations = invitations ? JSON.parse(invitations.replace(/'/g, '"')) : [];
    invitations.push(`${Math.floor(Date.now() / 1000)}`)
    return {
      ...employee,
      account_status: userStatusesConstants.PENDING,
      invitation_attempts: JSON.stringify(invitations)
    }
  },
  /**
   * Builds a formatted address string from company address data.
   * @param {Object} data - Address data object.
   * @param {string} [data.address] - Street address.
   * @param {string} [data.zip_code] - Postal / ZIP code.
   * @param {string} [data.city] - City name.
   * @param {string} [data.country] - Country name (will be capitalized).
   * @returns {string} A formatted address string.
   * @example
   * teamsHelpers.getCompanyAddress({ address: '1 Main St', zip_code: '75001', city: 'Paris', country: 'france' });
   */
  getCompanyAddress: (data) => {
    const {address, zip_code: zip, city, country} = data;
    const getProp = (prop, useComma = false) => prop ? `${prop}${useComma ? ',' : ''}` : '';
    const countryName = textHelpers.getCapitalized(getProp(country));
    return `${getProp(address, true)} ${getProp(zip)} ${getProp(city, true)} ${countryName}`;
  },
  /**
   * Returns true if the user role is ADMIN, BOOKKEEPER, or SUPPORT.
   * @param {string} userRole - A userRolesConstants value.
   * @returns {boolean} True if the role has admin-level access.
   * @example
   * teamsHelpers.isAdmin(userRolesConstants.ADMIN); // true
   */
  isAdmin: (userRole) => [ADMIN, BOOKKEEPER, SUPPORT].includes(userRole),
  /**
   * Returns true if the user role is MANAGER.
   * @param {string} userRole - A userRolesConstants value.
   * @returns {boolean} True if the role is a manager-level role.
   * @example
   * teamsHelpers.isManager(userRolesConstants.MANAGER); // true
   */
  isManager: (userRole) => userRole === MANAGER,
  /**
   * Returns true if the user role is BOOKKEEPER or SUPPORT.
   * @param {string} userRole - A userRolesConstants value.
   * @returns {boolean} True if the role is a support-level role.
   * @example
   * teamsHelpers.isSupport(userRolesConstants.SUPPORT); // true
   */
  isSupport: (userRole) => [BOOKKEEPER, SUPPORT].includes(userRole),
  /**
   * Returns the user's full name or email.
   * @param {Object|null} user - User object with full_name, email, and optional logo.
   * @returns {String} name, or null if no user.
   * @example
   * teamsHelpers.getEmployeeFullName({ full_name: 'Jane Doe', email: 'jane@example.com' });
   */
  getEmployeeFullName: (user) => {
    if (!user) return null;
    return user?.full_name || user?.email
  },
}
